/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { LOGGER_LOG_TYPE } from 'Config';
import { UserProfile } from 'api/account/models/UserProfile';
import DriversService from 'api/drivers/DriversService';
import { DriverDto } from 'api/drivers/models/DriverDto';
import UsersService from 'api/users/UsersService';
import Loading from 'common/services/Loading';
import Logger from 'common/services/Logger';
import { useEffect, useState } from 'react';
import { Breadcrumb, Button, Col, Form, Row } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Reducers } from 'store/types';
import styles from './DriverScreen.module.scss';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';

type Props = {
};

const DriverScreen: React.FC<Props> = () => {
    const { id, type } = useParams<{ id: string, type: string }>();
    const [isDetails, setIsDetails] = useState<boolean>(type === 'details');
    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasDriversWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['DRIVERS_WRITE']);
    const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false);
    const [itemToRemove, setItemToRemove] = useState<DriverDto | null>(null);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [validated, setValidated] = useState(false);
    const [item, setItem] = useState<DriverDto>();
    const [formData, setFormData] = useState({
        id: '',
        name: '',
        addressLine: '',
        zipCode: '',
        city: '',
        email: '',
        rowVersion: '',
    });

    const getData = async () => {
        try {
            let result: any = { id: '', name: '', addressLine: '', zipCode: '', city: '', email: '' };
            if (id) {
                Loading.show();
                result = await DriversService.getById(id);
                Loading.hide();
            }
            setItem(result);
            setFormData(result);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t get driver information', error);
            toast.error(t('messages.error_load_info'));
        }
    };

    useEffect(() => {
        getData().catch(console.error);
    }, [id, type]);

    function handleChange(e: any) {
        const key = e.target.name;
        const value = e.target.value;
        setFormData({ ...formData, [key]: value });
    }

    const navigateTo = (typeUrl?: string, id?: string) => {
        if (typeUrl) {
            navigate(`/driver/${typeUrl}/${id}`);
            setIsDetails(typeUrl === 'details');
        } else {
            navigate('/drivers');
        }
    }
    const onCancelRemove = () => {
        setItemToRemove(null);
        setShowRemoveModal(false);
    };

    const onRemove = async () => {
        if (itemToRemove === null) {
            toast.error(t('messages.record_delete_error'));
            return;
        }

        try {
            await DriversService.remove(itemToRemove);
            onCancelRemove();
            navigateTo();
            toast.success(t('messages.record_delete_success'));
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t delete driver', error);
            toast.error(t('messages.record_delete_error'));
        }
    };

    const showRemoveItemDialog = async (item: DriverDto) => {
        setItemToRemove(item);
        setShowRemoveModal(true);
    }

    const onSubmit = async (event: any) => {
        const form = event.currentTarget;
        event.preventDefault();
        event.stopPropagation();
        setValidated(true);
        if (form.checkValidity() === false) {
            return;
        }
        try {
            Loading.show();
            const model: DriverDto = {
                id: formData.id,
                name: formData.name,
                addressLine: formData.addressLine,
                zipCode: formData.zipCode,
                city: formData.city,
                email: formData.email,
                rowVersion: formData.rowVersion,
            };
            if (model && model.id) {
                await DriversService.update(model)
                navigateTo('details', id);
            } else if (model) {
                const id = await DriversService.create(model)
                navigateTo('details', id);
            }
            Loading.hide();
            toast.success(t('messages.record_save_success'));
        } catch (error: any) {
            if (error?.response?.status === 409) {
                toast(t('messages.driver_already_exists'));
            } else {
                Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't update the driver with id: ${form.id}`, error);
                toast.error(t('messages.record_save_error'));
            }
            Loading.hide();
        }
    };

    return (
        <ScreenTitle title={t('drivers.title')}>
            <Breadcrumb>
                <Breadcrumb.Item onClick={() => { navigateTo() }}>
                    {t('drivers.title')}
                </Breadcrumb.Item>
                <Breadcrumb.Item active>{t(`common.${type}`)}</Breadcrumb.Item>
            </Breadcrumb>
            <Form onSubmit={onSubmit} noValidate validated={validated}>
                <Row>
                    <Col xs={12} >
                        <Form.Group className="mb-3" controlId="name">
                            <Form.Label>{t('drivers.name')}{!isDetails ? '*' : ''}</Form.Label>
                            <Form.Control required name="name" type="text" defaultValue={item?.name} readOnly={isDetails} onChange={handleChange} placeholder={t('drivers.name')} />
                        </Form.Group>
                    </Col>
                    <Col xs={12} >
                        <Form.Group className="mb-3" controlId="address">
                            <Form.Label>{t('drivers.address')}</Form.Label>
                            <Form.Control name="addressLine" type="text" defaultValue={item?.addressLine} readOnly={isDetails} onChange={handleChange} placeholder={t('drivers.address')} />
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col xs={12} md={4}>
                        <Form.Group className="mb-3" controlId="zip_code">
                            <Form.Label>{t('drivers.zip_code')}</Form.Label>
                            <Form.Control name="zipCode" type="text" defaultValue={item?.zipCode} readOnly={isDetails} onChange={handleChange} placeholder={t('drivers.zip_code')} />
                        </Form.Group>
                    </Col>
                    <Col xs={12} md={8}>
                        <Form.Group className="mb-3" controlId="city">
                            <Form.Label>{t('drivers.city')}</Form.Label>
                            <Form.Control name="city" type="text" defaultValue={item?.city} readOnly={isDetails} onChange={handleChange} placeholder={t('drivers.city')} />
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col xs={12} md={4}>
                        <Form.Group className="mb-3" controlId="email">
                            <Form.Label>{t('drivers.email')}</Form.Label>
                            <Form.Control name="email" defaultValue={item?.email} type="text" readOnly={isDetails} onChange={handleChange} placeholder={t('drivers.email')} />
                        </Form.Group>
                    </Col>
                </Row>
                <div className={styles.buttonsContainer}>
                    <Button variant="secondary" type="button" className={styles.button} onClick={() => navigateTo()}>
                        {t('common.cancel')}
                    </Button>
                    {isDetails && hasDriversWritePolicy &&
                        <Button variant="danger" type="button" className={styles.button} onClick={() => showRemoveItemDialog(item as DriverDto)} >
                            {t('common.delete')}
                        </Button>
                    }
                    {isDetails && hasDriversWritePolicy &&
                        <Button variant="warning" type="button" className={styles.button} onClick={() => { navigateTo('edit', item?.id); }}>
                            {t('common.edit')}
                        </Button>
                    }
                    {!isDetails && hasDriversWritePolicy &&
                        <Button variant="primary" type="submit" className={styles.button}>
                            {t('common.save')}
                        </Button>
                    }
                </div>
            </Form>
            <QuestionYesNo onNo={onCancelRemove} onYes={onRemove} isVisible={showRemoveModal} message={t('messages.remove_record_with_ident', { name: item?.name ?? '' })} />
        </ScreenTitle>
    );
};

export default DriverScreen;
